
import { useState, useEffect } from 'react';
import { Droplet, Shield, Award, Users, PhoneCall, Mail, MapPin, ChevronRight, Star, Check, BarChart } from 'lucide-react';
import NavBar from '../components/Navbar';
import Footer from '../components/Footer';

export default function AboutUs() {
  const [activeTab, setActiveTab] = useState('mission');
  const [isVisible, setIsVisible] = useState({
    hero: false,
    story: false,
    tabs: false,
    features: false,
    stats: false,
    team: false,
    testimonials: false,
    contact: false
  });
  
  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    };
    
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    const sections = ['hero', 'story', 'tabs', 'features', 'stats', 'team', 'testimonials', 'contact'];
    sections.forEach(section => {
      const element = document.getElementById(section);
      if (element) observer.observe(element);
    });
    
    return () => {
      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) observer.unobserve(element);
      });
    };
  }, []);
  
  // Animation classes
  const getAnimationClass = (section) => {
    return isVisible[section] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10';
  };

  // Team members data
  const teamMembers = [
    {
      name: "Alex Johnson",
      role: "Founder & CEO",
      image: "/api/placeholder/300/300",
      bio: "Water quality expert with 15+ years of experience in the industry."
    },
    {
      name: "Morgan Smith",
      role: "Technical Director",
      image: "/api/placeholder/300/300",
      bio: "Engineer specializing in advanced filtration technologies."
    },
    {
      name: "Taylor Williams",
      role: "Customer Experience",
      image: "/api/placeholder/300/300",
      bio: "Dedicated to ensuring seamless service from consultation to installation."
    }
  ];
  
  // Testimonials data
  const testimonials = [
    {
      text: "Installing a PureFlow water filtration system has been one of the best decisions we've made for our home. The water tastes amazing, and knowing our family is drinking clean, safe water gives us incredible peace of mind.",
      author: "Sarah M.",
      location: "San Francisco, CA"
    },
    {
      text: "The difference in water quality is remarkable. No more bottled water for us! The PureFlow team was professional from start to finish, and the installation was quick and clean.",
      author: "James T.",
      location: "Austin, TX"
    },
    {
      text: "As someone with sensitive skin, I've noticed a huge improvement since installing my whole-house filter. The customer service team has been exceptional with follow-up and maintenance reminders.",
      author: "Laura B.",
      location: "Denver, CO"
    }
  ];
  
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  // Stats data
  const stats = [
    { label: "Homes Served", value: "10,000+", icon: <Users size={24} className="text-blue-500" /> },
    { label: "Certified Technicians", value: "25+", icon: <Award size={24} className="text-blue-500" /> },
    { label: "Years Experience", value: "15+", icon: <BarChart size={24} className="text-blue-500" /> },
    { label: "5-Star Reviews", value: "2,500+", icon: <Star size={24} className="text-blue-500" /> }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 overflow-hidden">

        <NavBar />
      {/* Hero Section - Modern Glassmorphism Style */}
      <div id="hero" className={`relative bg-gradient-to-r from-blue-600 to-blue-500 text-white overflow-hidden transition-all duration-1000 ${getAnimationClass('hero')}`}>
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute top-1/2 -right-24 w-80 h-80 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"></div>
          <div className="absolute -bottom-32 left-1/2 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-60"></div>
        </div>
        
        {/* Content */}
        <div className="container mx-auto px-4 py-24 md:py-32 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">About PureFlow</h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed font-light">
              We're revolutionizing home water filtration with cutting-edge technology and exceptional service.
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-full font-medium hover:bg-opacity-90 transition-all duration-300 inline-flex items-center group">
              Our Mission <ChevronRight size={18} className="ml-1 transform group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
        
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" className="w-full text-gray-50 fill-current">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"></path>
          </svg>
        </div>
      </div>
      
      {/* Company Overview - Modern Card Design */}
      <div id="story" className={`container mx-auto px-4 py-20 transition-all duration-1000 delay-200 ${getAnimationClass('story')}`}>
        <div className="grid md:grid-cols-5 gap-12 items-center">
          <div className="md:col-span-3">
            <h2 className="text-3xl font-bold mb-8 text-blue-700 border-b-2 border-blue-500 pb-2 inline-block">Our Story</h2>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Home Water Systems began with a simple mission: to make clean, healthy water accessible to every household. What started as a small family business has grown into a trusted provider of premium water filtration solutions.
            </p>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Our founder, after experiencing water quality issues in their own home, recognized the need for affordable, reliable filtration systems that truly worked. This personal experience ignited a passion for water quality that continues to drive our company today.
            </p>
          
          </div>
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-5 text-white">
                <Droplet size={40} className="mb-2" />
                <h3 className="text-2xl font-semibold">Our Core Values</h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-full p-2 mr-4 text-blue-600">
                    <Shield size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-1">Integrity</h4>
                    <p className="text-gray-600 text-sm">We're transparent about our products, pricing, and processes.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-full p-2 mr-4 text-blue-600">
                    <Check size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-1">Quality</h4>
                    <p className="text-gray-600 text-sm">We never compromise on the quality of our filtration systems.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-full p-2 mr-4 text-blue-600">
                    <Users size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-700 mb-1">Customer-First</h4>
                    <p className="text-gray-600 text-sm">Your satisfaction and health are our top priorities.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs Section - Modern Tab Design */}
      <div id="tabs" className={`bg-white py-20 transition-all duration-1000 delay-300 ${getAnimationClass('tabs')}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-700">Why Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              We're dedicated to excellence in every aspect of water filtration
            </p>
          </div>
          
          {/* Modern Tab Navigation */}
          <div className="flex flex-wrap justify-center mb-10 max-w-3xl mx-auto">
            {['mission', 'expertise', 'quality', 'team'].map((tab) => (
              <button 
                key={tab}
                className={`px-6 py-3 mx-2 mb-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeTab === tab 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Tab Content with Modern Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-100">
              {activeTab === 'mission' && (
                <div className="animate-fadeIn">
                  <div className="flex items-center mb-6">
                    <div className="bg-blue-100 p-4 rounded-full mr-5">
                      <Droplet size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-blue-700">Our Mission</h3>
                  </div>
                  <p className="mb-6 text-gray-700 leading-relaxed">
                    At here, our mission is to empower families to take control of their water quality through innovative filtration solutions. We believe everyone deserves access to clean, safe water that tastes great and promotes health and wellbeing.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Beyond providing products, we're committed to educating consumers about water quality issues and serving as trusted advisors in helping them find the right solutions for their specific needs and budgets.
                  </p>
                </div>
              )}
              
              {activeTab === 'expertise' && (
                <div className="animate-fadeIn">
                  <div className="flex items-center mb-6">
                    <div className="bg-blue-100 p-4 rounded-full mr-5">
                      <Award size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-blue-700">Our Expertise</h3>
                  </div>
                  <p className="mb-6 text-gray-700 leading-relaxed">
                    With over a decade in the water treatment industry, our team brings extensive knowledge of filtration technologies, water chemistry, and system design. Our water quality specialists undergo rigorous training and certification to ensure they can address even the most complex water concerns.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    We stay at the forefront of water treatment innovations, constantly evaluating new technologies and methods to provide our customers with cutting-edge solutions that deliver exceptional results.
                  </p>
                </div>
              )}
              
              {activeTab === 'quality' && (
                <div className="animate-fadeIn">
                  <div className="flex items-center mb-6">
                    <div className="bg-blue-100 p-4 rounded-full mr-5">
                      <Shield size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-blue-700">Our Commitment to Quality</h3>
                  </div>
                  <p className="mb-6 text-gray-700 leading-relaxed">
                    We never compromise on quality. Every filtration system we offer undergoes rigorous testing to ensure it meets our strict standards for performance, durability, and efficiency. We partner with leading manufacturers who share our commitment to excellence.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Our systems are certified by independent laboratories to remove specific contaminants, and we maintain transparency about what our products can and cannot do. We stand behind everything we sell with comprehensive warranties and dedicated after-sales support.
                  </p>
                </div>
              )}
              
              {activeTab === 'team' && (
                <div className="animate-fadeIn">
                  <div className="flex items-center mb-6">
                    <div className="bg-blue-100 p-4 rounded-full mr-5">
                      <Users size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-blue-700">Our Team</h3>
                  </div>
                  <p className="mb-6 text-gray-700 leading-relaxed">
                    Our greatest asset is our people. The PureFlow team consists of passionate water quality experts, experienced technicians, and customer service professionals who genuinely care about making a difference in our customers' lives.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    From your first consultation to installation and beyond, you'll work with knowledgeable, friendly professionals who listen to your concerns and work diligently to ensure your complete satisfaction. Many of our team members have been with us for years, reflecting our commitment to creating a positive workplace built on shared values.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Section */}
      {/* <div id="stats" className={`container mx-auto px-4 py-20 transition-all duration-1000 delay-400 ${getAnimationClass('stats')}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center transform hover:-translate-y-1 transition-all duration-300">
                <div className="mx-auto bg-blue-50 w-16 h-16 flex items-center justify-center rounded-full mb-4">
                  {stat.icon}
                </div>
                <h3 className="text-3xl font-bold text-blue-700 mb-2">{stat.value}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div> */}
      
      {/* Meet The Team Section */}
      {/* <div id="team" className={`bg-gradient-to-b from-gray-50 to-white py-20 transition-all duration-1000 delay-500 ${getAnimationClass('team')}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-700">Meet Our Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              The experts behind our success
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={index} className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg bg-white transition-all duration-300 group-hover:shadow-xl">
                  <div className="relative h-64 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900 to-transparent opacity-60 z-10"></div>
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-4 left-4 text-white z-20">
                      <h3 className="text-xl font-bold">{member.name}</h3>
                      <p className="text-blue-200">{member.role}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600">{member.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div> */}
      
      {/* Testimonials Carousel */}
      <div id="testimonials" className={`bg-white py-20 transition-all duration-1000 delay-600 ${getAnimationClass('testimonials')}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-700">What Our Customers Say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Real feedback from real customers
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            {/* Testimonial Slider */}
            <div className="relative">
              <div className="overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl shadow-xl text-white">
                <div className="md:grid md:grid-cols-5">
                  {/* Decorative Elements */}
                  <div className="hidden md:block md:col-span-2 relative overflow-hidden">
                    <div className="absolute inset-0">
                      <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-400 rounded-full mix-blend-overlay filter blur-3xl opacity-70"></div>
                      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-blue-300 rounded-full mix-blend-overlay filter blur-3xl opacity-60"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="p-8">
                        <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-6">
                          <Droplet size={32} />
                        </div>
                        <blockquote className="text-2xl font-light italic">"Pure water is the world's first and foremost medicine."</blockquote>
                      </div>
                    </div>
                  </div>
                  
                  {/* Testimonial Content */}
                  <div className="p-8 md:p-12 md:col-span-3">
                    <div className="text-5xl text-white mb-6 opacity-30">"</div>
                    <p className="text-lg md:text-xl mb-8 leading-relaxed">
                      {testimonials[activeTestimonial].text}
                    </p>
                    <div>
                      <p className="font-semibold text-lg">{testimonials[activeTestimonial].author}</p>
                      <p className="text-blue-100">{testimonials[activeTestimonial].location}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Navigation Dots */}
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button 
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      activeTestimonial === index ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    onClick={() => setActiveTestimonial(index)}
                    aria-label={`Testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Section - Modern Design */}
      <div id="contact" className={`bg-gradient-to-b from-gray-50 to-gray-100 py-20 transition-all duration-1000 delay-700 ${getAnimationClass('contact')}`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-700">Get In Touch</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Have questions or ready to explore water filtration solutions for your home? Our team is here to help.
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="md:grid md:grid-cols-2">
                {/* Contact Form */}
                <div className="p-8 md:p-12">
                  <h3 className="text-2xl font-bold text-blue-700 mb-6">Send Us a Message</h3>
                  <form className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">Name</label>
                        <input 
                          type="text" 
                          id="name" 
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300" 
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">Email</label>
                        <input 
                          type="email" 
                          id="email" 
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300" 
                          placeholder="Your email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="subject">Subject</label>
                      <input 
                        type="text" 
                        id="subject" 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300" 
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="message">Message</label>
                      <textarea 
                        id="message" 
                        rows="4" 
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300" 
                        placeholder="Tell us more about your needs..."
                      ></textarea>
                    </div>
                    <button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-300"
                    >
                      Send Message
                    </button>
                  </form>
                </div>
                
                {/* Contact Info */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-8 md:p-12 text-white">
                  <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg mr-4">
                        <PhoneCall size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-blue-100 mb-1">Call Us</p>
                        <p className="font-medium text-lg">(123) 456-7890</p>
                        <p className="text-sm text-blue-100 mt-1">Mon-Fri: 8am-6pm</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg mr-4">
                        <Mail size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-blue-100 mb-1">Email Us</p>
                        <p className="font-medium text-lg">info@pureflowwater.com</p>
                        <p className="text-sm text-blue-100 mt-1">We respond within 24 hours</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="bg-white bg-opacity-10 p-3 rounded-lg mr-4">
                        <MapPin size={24} />
                      </div>
                      <div>
                        <p className="text-sm text-blue-100 mb-1">Visit Us</p>
                        <p className="font-medium text-lg">123 Water Lane</p>
                        <p className="text-sm">Clearwater, CA 90210</p>
                      </div>
                    </div>
                  </div>
                  </div>
                  </div>
                  </div>
                  </div>

                  </div>
                  </div>
    
    <Footer />
    </div>
    
  )
}
                  
                  {/* Social Media */}
                  {/* <div className="mt-12">
                    <p className="mb-4">Connect With Us</p>
                    <div className="flex space-x-4">
                      <a href="#" className="bg-white bg-opacity-10 p-3 rounded-lg hover:bg-opacity-20 transition-all duration-300">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                        </svg>
                      </a>
                      <a href="#" className="bg-white bg-opacity-10 p-3 rounded-lg hover:bg-opacity-20 transition-all duration-300">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 */}